/**
 * O contrato da API de preços públicos (`bff/v1/admin/precos-publicos`,
 * migração 0320). Os nomes são os da resposta; o dinheiro viaja como texto
 * decimal em reais, nunca como número de ponto flutuante (CLAUDE.md, "Regras de
 * banco", item 7).
 */

/**
 * Os serviços administrativos que podem ter preço público.
 *
 * Espelha o tipo enumerado `tipo_servico_administrativo` de
 * `db/migrations/0002_dominio_base.sql` e a lista de
 * `apps/api/src/modulos/precos-publicos/precos-publicos.dto.ts` — conjunto
 * fechado de valores vira tipo enumerado (CLAUDE.md, "Regras de banco", item 8).
 * Manter os três lados em sincronia é obrigação de quem alterar o enum.
 */
export const TIPOS_DE_SERVICO_ADMINISTRATIVO = [
  'SUBSTITUICAO_CONDUTOR',
  'SUBSTITUICAO_MONITOR',
  'SUBSTITUICAO_VEICULO',
  'REVISAO_BANCADA',
  'VISTORIA_CRECHE',
  'TROCA_DRE',
  'INCLUSAO_VEICULO',
  'RECURSO_ADMINISTRATIVO',
  'TROCA_TRIPLA',
] as const;

export type TipoDeServicoAdministrativo = (typeof TIPOS_DE_SERVICO_ADMINISTRATIVO)[number];

/** O rótulo em português de cada serviço, para não mostrar o valor cru do enum. */
export const ROTULO_DO_SERVICO: Record<TipoDeServicoAdministrativo, string> = {
  SUBSTITUICAO_CONDUTOR: 'Substituição de condutor',
  SUBSTITUICAO_MONITOR: 'Substituição de monitor',
  SUBSTITUICAO_VEICULO: 'Substituição de veículo',
  REVISAO_BANCADA: 'Revisão de bancada',
  VISTORIA_CRECHE: 'Vistoria de creche',
  TROCA_DRE: 'Troca de DRE',
  INCLUSAO_VEICULO: 'Inclusão de veículo',
  RECURSO_ADMINISTRATIVO: 'Recurso administrativo',
  TROCA_TRIPLA: 'Troca tripla',
};

/**
 * Onde o preço está no tempo, calculada pelo servidor com a data de hoje em São
 * Paulo: a tela não refaz a conta, para não divergir de quem cobra.
 */
export type SituacaoDoPreco = 'VIGENTE' | 'AGENDADO' | 'ENCERRADO' | 'INATIVO';

export const ROTULO_DA_SITUACAO: Record<SituacaoDoPreco, string> = {
  VIGENTE: 'Vigente',
  AGENDADO: 'Agendado',
  ENCERRADO: 'Encerrado',
  INATIVO: 'Inativo',
};

export interface PrecoPublico {
  id: string;
  tipoServico: TipoDeServicoAdministrativo;
  descricao: string;
  /** Reais, texto decimal (`128.40`). */
  valor: string;
  decretoReferencia: string | null;
  /** `AAAA-MM-DD`. */
  vigenciaInicio: string;
  vigenciaFim: string | null;
  ativo: boolean;
  situacao: SituacaoDoPreco;
}

export interface ListaDePrecos {
  itens: PrecoPublico[];
  total: number;
  pagina: number;
  tamanho: number;
}

export interface EntradaDoPreco {
  tipoServico: TipoDeServicoAdministrativo;
  descricao: string;
  valor: string;
  decretoReferencia?: string;
  vigenciaInicio: string;
  vigenciaFim?: string;
}
